import { signOut } from "firebase/auth";
import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import { apiClient } from "@/api/apiClient";
import { auth } from "@/plugins/firebase";
import router from "@/router";

Vue.use(Vuex);

const getDefaultState = () => {
  return {
    uid: null,
    customerId: null,
    email: null,
    idToken: null,
    isCreatingUser: false,
    isLoadingUser: false,
    authStatus: "pending" as "pending" | "authenticated" | "unauthenticated",
    authError: null as string | null,
  };
};

export default new Vuex.Store({
  state: getDefaultState(),
  mutations: {
    setUid(state, uid) {
      state.uid = uid;
    },
    setCustomerId(state, customerId) {
      state.customerId = customerId;
    },
    setEmail(state, email) {
      state.email = email;
    },
    setIdToken(state, idToken) {
      state.idToken = idToken;
    },
    setIsCreatingUser(state, isCreatingUser) {
      state.isCreatingUser = isCreatingUser;
    },
    setIsLoadingUser(state, isLoadingUser) {
      state.isLoadingUser = isLoadingUser;
    },
    setAuthStatus(
      state,
      status: "pending" | "authenticated" | "unauthenticated",
    ) {
      state.authStatus = status;
    },
    setAuthError(state, error: string | null) {
      state.authError = error;
    },
    reset(state) {
      // Preserve authError before reset so it can be shown on sign-in page
      const authError = state.authError;

      // Clear persisted state from localStorage first
      localStorage.removeItem("langx-customer-console");
      localStorage.removeItem("accessToken");

      // Reset state to default values
      Object.assign(state, getDefaultState());

      // Restore authError after reset
      state.authError = authError;
    },
  },
  getters: {
    uid: (state) => (state.uid ? state.uid : null),
    customerId: (state) => (state.customerId ? state.customerId : null),
    email: (state) => (state.email ? state.email : null),
    idToken: (state) => (state.idToken ? state.idToken : null),
    isCreatingUser: (state) =>
      state.isCreatingUser ? state.isCreatingUser : false,
    isLoadingUser: (state) =>
      state.isLoadingUser ? state.isLoadingUser : false,
    authStatus: (state) => state.authStatus,
    isAuthenticated: (state) => state.authStatus === "authenticated",
    authError: (state) => state.authError,
  },
  actions: {
    clearApiClient() {
      // Clear API client state
      apiClient.destroy();
    },
    async signOut({ commit }, errorReason?: string) {
      try {
        await signOut(auth);
        commit("reset");

        // Clear API client state
        apiClient.destroy();

        // Clear all localStorage data that could contain user information
        localStorage.removeItem("langx-customer-console");
        localStorage.removeItem("accessToken");

        if (router.currentRoute.name !== "Sign In") {
          // If there's an error reason, set it before navigating
          if (errorReason) {
            commit("setAuthError", errorReason);
          }
          router.replace({
            name: "Sign In",
          });
        } else if (errorReason) {
          // Already on sign-in page, just set the error
          commit("setAuthError", errorReason);
        }
      } catch (error) {
        // Properly type check and handle error
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        console.error("[Auth] Sign out failed:", errorMessage);
      }
    },
  },
  modules: {},
  // リロード時にstore.stateを維持
  // authからstore.stateに代入している情報をauthから直接参照する場合は削除しても動作に影響なし
  // セキュリティ向上（特にユーザがサインアウトせずブラウザを閉じた場合）のためセッションストレージを指定、および攻撃を防ぐためキー名がvuexとなることを隠蔽
  plugins: [
    createPersistedState({
      key: "langx-customer-console",
      storage: window.localStorage,
    }),
  ],
});
